import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError } from 'rxjs/operators';
import { Observable, Subject, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { Activityfeedpost, createEmptyActivityfeedpost } from '../models/activityfeedpost';
import { ACTIVITY_ENTITY, ACTIVITY_MODULE, ACTIVITY_VERB, getVerbLabel, getEntityLabel, getModuleLabel } from '../constants/activity-refs';

@Injectable({
    providedIn: 'root'
})
export class ActivityService {

    serverUrl = environment.serverUrl;
    httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    //private http: HttpClient;
    private static cacheBuster$: Subject<void> = new Subject<void>();
    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
        }
        // return an observable with a user-facing error message
        return throwError('Something bad happened. Please try again later.');

    }

    user_id: number;


    constructor(handler: HttpBackend, private http: HttpClient, public authService: AuthService) {
        //this.http = new HttpClient(handler);
    }

    /** Retourne la date/heure locale (YYYY-MM-DD HH:mm:ss), pas UTC. Évite le décalage d'1h sur les actions. */
    private formatLocalDateTime(): string {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }

    private normalizeToken(value: string | undefined | null): string {
        return String(value ?? '').trim().toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
    }

    private resolveVerbId(type: string, action: string): number | undefined {
        const t = this.normalizeToken(type);
        const a = this.normalizeToken(action);
        if (t === 'view' || a.includes('view')) return ACTIVITY_VERB.VIEW;
        if (t === 'download' || a.includes('download')) return ACTIVITY_VERB.DOWNLOAD;
        if (t === 'update' || t === 'edit' || a.includes('update') || a.includes('edit')) return ACTIVITY_VERB.UPDATE;
        if (t === 'delete' || a.includes('delete') || a.includes('remove')) return ACTIVITY_VERB.DELETE;
        if (t === 'add' || a.includes('add')) return ACTIVITY_VERB.ADD;
        if (t === 'create' || a.includes('create')) return ACTIVITY_VERB.CREATE;
        if (t === 'validate' || a.includes('validate')) return ACTIVITY_VERB.VALIDATE;
        if (t === 'import' || a.includes('import')) return ACTIVITY_VERB.IMPORT;
        if (t === 'reject' || a.includes('reject')) return ACTIVITY_VERB.REJECT;
        return undefined;
    }

    private resolveEntityId(categorie: string): number | undefined {
        const c = this.normalizeToken(categorie);
        const map: Record<string, number> = {
            opportunity: ACTIVITY_ENTITY.OPPORTUNITY,
            deal: ACTIVITY_ENTITY.DEAL,
            appointment: ACTIVITY_ENTITY.APPOINTMENT,
            corporate: ACTIVITY_ENTITY.CORPORATE,
            contact: ACTIVITY_ENTITY.CONTACT,
            lender: ACTIVITY_ENTITY.LENDER,
            tranche: ACTIVITY_ENTITY.TRANCHE,
            clause: ACTIVITY_ENTITY.CLAUSE,
            role_afs: ACTIVITY_ENTITY.ROLE_AFS,
            'role afs': ACTIVITY_ENTITY.ROLE_AFS,
            contact_deal: ACTIVITY_ENTITY.CONTACT_DEAL,
            'contact deal': ACTIVITY_ENTITY.CONTACT_DEAL,
            trade: ACTIVITY_ENTITY.TRADE,
            draw_down: ACTIVITY_ENTITY.DRAW_DOWN,
            'draw down': ACTIVITY_ENTITY.DRAW_DOWN,
            margin: ACTIVITY_ENTITY.MARGIN,
            interest: ACTIVITY_ENTITY.INTEREST,
            document_kyc: ACTIVITY_ENTITY.DOCUMENT_KYC,
            'document kyc': ACTIVITY_ENTITY.DOCUMENT_KYC,
            document_company: ACTIVITY_ENTITY.DOCUMENT_COMPANY,
            'document company': ACTIVITY_ENTITY.DOCUMENT_COMPANY,
            document_lender: ACTIVITY_ENTITY.DOCUMENT_LENDER,
            'document lender': ACTIVITY_ENTITY.DOCUMENT_LENDER,
            organization_contact: ACTIVITY_ENTITY.ORGANIZATION_CONTACT,
            'organization contact': ACTIVITY_ENTITY.ORGANIZATION_CONTACT,
            timesheet: ACTIVITY_ENTITY.TIMESHEET,
            timesheet_group: ACTIVITY_ENTITY.TIMESHEET_GROUP,
            'timesheet group': ACTIVITY_ENTITY.TIMESHEET_GROUP,
            invoice: ACTIVITY_ENTITY.INVOICE,
            fee: ACTIVITY_ENTITY.FEE,
            fixed_fee: ACTIVITY_ENTITY.FIXED_FEE,
            'fixed fee': ACTIVITY_ENTITY.FIXED_FEE,
            product_fee: ACTIVITY_ENTITY.PRODUCT_FEE,
            'product fee': ACTIVITY_ENTITY.PRODUCT_FEE,
            iban_jm: ACTIVITY_ENTITY.IBAN_JM,
            'iban jm': ACTIVITY_ENTITY.IBAN_JM,
            user: ACTIVITY_ENTITY.USER,
            users: ACTIVITY_ENTITY.USER,
            role: ACTIVITY_ENTITY.ROLE,
            roles: ACTIVITY_ENTITY.ROLE,
            role_afs_config: ACTIVITY_ENTITY.ROLE_AFS_CONFIG,
            'role afs config': ACTIVITY_ENTITY.ROLE_AFS_CONFIG,
            excel: ACTIVITY_ENTITY.EXCEL,
            overview: ACTIVITY_ENTITY.BILLING_OVERVIEW,
            'billing overview': ACTIVITY_ENTITY.BILLING_OVERVIEW,
            'invoice tracking': ACTIVITY_ENTITY.INVOICE_TRACKING,
            'pennylane history': ACTIVITY_ENTITY.PENNYLANE_HISTORY,
            'legal documents': ACTIVITY_ENTITY.KYC_LEGAL_DOCUMENTS,
            organization: ACTIVITY_ENTITY.KYC_ORGANIZATION,
            'contacts kyc': ACTIVITY_ENTITY.KYC_CONTACTS_KYC,
            'kyc history': ACTIVITY_ENTITY.KYC_HISTORY,
            activities: ACTIVITY_ENTITY.KYC_ACTIVITIES,
            executive: ACTIVITY_ENTITY.EXECUTIVE,
            mandat: ACTIVITY_ENTITY.MANDAT,
            status: ACTIVITY_ENTITY.STATUS,
            contact_role: ACTIVITY_ENTITY.CONTACT_ROLE,
            config_api: ACTIVITY_ENTITY.CONFIG_API,
            gemini_chat: ACTIVITY_ENTITY.GEMINI_CHAT,
            snapshot_calendar: ACTIVITY_ENTITY.SNAPSHOT_CALENDAR,
            'snapshot calendar': ACTIVITY_ENTITY.SNAPSHOT_CALENDAR,
        };
        return map[c];
    }

    private resolveModuleId(section: string): number | undefined {
        const s = this.normalizeToken(section);
        const map: Record<string, number> = {
            admin: ACTIVITY_MODULE.ADMIN,
            crm: ACTIVITY_MODULE.CRM,
            execution: ACTIVITY_MODULE.EXECUTION,
            kyc: ACTIVITY_MODULE.KYC,
            'kyc company': ACTIVITY_MODULE.KYC_COMPANY,
            'kyc lender': ACTIVITY_MODULE.KYC_LENDER,
            billing: ACTIVITY_MODULE.BILLING,
            payment: ACTIVITY_MODULE.PAYMENT,
            contact: ACTIVITY_MODULE.CONTACT,
            kpi: ACTIVITY_MODULE.KPI,
            all_companies: ACTIVITY_MODULE.ALL_COMPANIES,
            all_contacts: ACTIVITY_MODULE.ALL_CONTACTS,
            all_lenders: ACTIVITY_MODULE.ALL_LENDERS,
            'all companies': ACTIVITY_MODULE.ALL_COMPANIES,
            'all contacts': ACTIVITY_MODULE.ALL_CONTACTS,
            'all lenders': ACTIVITY_MODULE.ALL_LENDERS,
            companies: ACTIVITY_MODULE.ALL_COMPANIES
        };
        return map[s];
    }

    activityfeedpost: Activityfeedpost;

    track_activity(action: string, categorie: string, section: string, type: string, url: string, options?: { notes?: string; verb_id?: number; entity_id?: number; module_id?: number; target_id?: number; slug_id?: string; metadata?: Record<string, any> | string }) {
        this.activityfeedpost = createEmptyActivityfeedpost();
        const currentUser = this.authService.currentUser();
        this.user_id = currentUser.id;

        this.activityfeedpost.user_id = this.user_id;
        this.activityfeedpost.action = action;
        this.activityfeedpost.categorie = categorie;
        this.activityfeedpost.section = section;
        this.activityfeedpost.type = type;
        this.activityfeedpost.url = url;
        this.activityfeedpost.date_time = this.formatLocalDateTime();

        if (options) {
            if (options.notes != null && options.notes !== '') this.activityfeedpost.notes = options.notes;
            if (options.verb_id != null) this.activityfeedpost.verb_id = options.verb_id;
            if (options.entity_id != null) this.activityfeedpost.entity_id = options.entity_id;
            if (options.module_id != null) this.activityfeedpost.module_id = options.module_id;
            if (options.target_id != null) this.activityfeedpost.target_id = options.target_id;
            if (options.slug_id != null && options.slug_id !== '') this.activityfeedpost.slug_id = options.slug_id;
            if (options.metadata != null && options.metadata !== '') this.activityfeedpost.metadata = options.metadata;
        }

        this.add_new_activity_feed(this.activityfeedpost).subscribe({
            next: (res: any) => {
                if (res && res.status && res.status !== 'success') {
                    console.error('track_activity insert failed:', res);
                }
            },
            error: (err) => console.error('track_activity HTTP error:', err)
        });
    }

    /**
     * Nouvelle version du tracking (target_id, slug_id) - migration progressive.
     * À migrer successivement depuis track_activity.
     */
    track_activity2(action: string, categorie: string, section: string, type: string, url: string, options?: { notes?: string; verb_id?: number; entity_id?: number; module_id?: number; target_id?: number; slug_id?: string; metadata?: Record<string, any> | string }) {
        this.activityfeedpost = createEmptyActivityfeedpost();
        const currentUser = this.authService.currentUser();
        this.user_id = currentUser.id;

        this.activityfeedpost.user_id = this.user_id;
        this.activityfeedpost.action = action;
        this.activityfeedpost.categorie = categorie;
        this.activityfeedpost.section = section;
        this.activityfeedpost.type = type;
        this.activityfeedpost.url = url;
        this.activityfeedpost.date_time = this.formatLocalDateTime();

        // Toujours stocker les IDs refs quand disponibles (migration progressive).
        this.activityfeedpost.verb_id = this.resolveVerbId(type, action);
        this.activityfeedpost.entity_id = this.resolveEntityId(categorie);
        this.activityfeedpost.module_id = this.resolveModuleId(section);

        if (options) {
            if (options.notes != null && options.notes !== '') this.activityfeedpost.notes = options.notes;
            if (options.verb_id != null) this.activityfeedpost.verb_id = options.verb_id;
            if (options.entity_id != null) this.activityfeedpost.entity_id = options.entity_id;
            if (options.module_id != null) this.activityfeedpost.module_id = options.module_id;
            if (options.target_id != null) this.activityfeedpost.target_id = options.target_id;
            if (options.slug_id != null && options.slug_id !== '') this.activityfeedpost.slug_id = options.slug_id;
            if (options.metadata != null && options.metadata !== '') this.activityfeedpost.metadata = options.metadata;
        }

        this.add_new_activity_feed2(this.activityfeedpost).subscribe({
            next: (res: any) => {
                if (res && res.status && res.status !== 'success') {
                    console.error('track_activity2 insert failed:', res);
                }
            },
            error: (err) => console.error('track_activity2 HTTP error:', err)
        });
    }

    /**
     * Nouvelle version du tracking par refs (utilise track_activity2 / add_new_activity_feed2).
     * Pour migration progressive depuis trackActivityByRefs.
     */
    trackActivityByRefs2(
        verbId: number,
        entityId: number,
        moduleId: number,
        url: string,
        options?: { target_id?: number; slug_id?: string; metadata?: Record<string, any> | string }
    ): void {
        const action = `${getVerbLabel(verbId)} ${getEntityLabel(entityId)}`;
        const categorie = getEntityLabel(entityId);
        const section = getModuleLabel(moduleId);
        const type = getVerbLabel(verbId).toLowerCase();
        this.track_activity2(action, categorie, section, type, url, {
            verb_id: verbId,
            entity_id: entityId,
            module_id: moduleId,
            ...options
        });
    }

    /**
     * Tracking dynamique via les constantes (verb_id, entity_id, module_id).
     * Les libellés action, categorie, section, type sont dérivés du schéma.
     */
    /**
     * Tracks contact creation from AddContactStepper close payload (slug pair, role, target_id).
     */
    trackContactCreateActivity(
        url: string,
        result: any,
        options?: {
            action?: string;
            moduleId?: number;
            entityId?: number;
            source?: string;
        }
    ): void {
        const moduleId = options?.moduleId ?? ACTIVITY_MODULE.CONTACT;
        const sectionByModule: Record<number, string> = {
            [ACTIVITY_MODULE.CRM]: 'crm',
            [ACTIVITY_MODULE.EXECUTION]: 'execution',
            [ACTIVITY_MODULE.KYC]: 'kyc',
            [ACTIVITY_MODULE.KYC_COMPANY]: 'kyc',
            [ACTIVITY_MODULE.KYC_LENDER]: 'kyc',
            [ACTIVITY_MODULE.CONTACT]: 'contact',
            [ACTIVITY_MODULE.BILLING]: 'billing',
        };
        const section = sectionByModule[moduleId] ?? 'contact';

        const corp = String(result?.corporate_slug ?? result?.data?.corporate_slug ?? '').trim();
        const contactSlug = String(result?.data?.slug ?? result?.contact_slug ?? '').trim();
        const slugPair =
            corp && contactSlug ? `${corp}/${contactSlug}` : (contactSlug || corp || undefined);
        const roleLabel = String(result?.role_names ?? '').trim() || 'N/A';
        const targetId = result?.data?.id ?? result?.contact_id ?? undefined;

        const metadata: Record<string, string> = { role: roleLabel };
        if (options?.source) {
            metadata['source'] = options.source;
        }

        this.track_activity2(
            options?.action ?? 'Create contact',
            'contact',
            section,
            'add',
            url,
            {
                verb_id: ACTIVITY_VERB.CREATE,
                entity_id: options?.entityId ?? ACTIVITY_ENTITY.CONTACT,
                module_id: moduleId,
                slug_id: slugPair,
                target_id: targetId,
                metadata,
            }
        );
    }

    /**
     * Tracks lender consultation (view) with composite slug corporate/lender when available.
     */
    trackLenderViewActivity(
        url: string,
        corporateSlug: string | undefined | null,
        lenderSlug: string | undefined | null,
        options?: {
            action?: string;
            moduleId?: number;
            source?: string;
        }
    ): void {
        const corp = String(corporateSlug ?? '').trim();
        const lender = String(lenderSlug ?? '').trim();
        if (!corp && !lender) {
            return;
        }

        const moduleId = options?.moduleId ?? ACTIVITY_MODULE.ALL_LENDERS;
        const sectionByModule: Record<number, string> = {
            [ACTIVITY_MODULE.ALL_LENDERS]: 'contact',
            [ACTIVITY_MODULE.CONTACT]: 'contact',
            [ACTIVITY_MODULE.KYC]: 'kyc',
            [ACTIVITY_MODULE.KYC_LENDER]: 'kyc',
        };
        const section = sectionByModule[moduleId] ?? 'contact';
        const slugPair = corp && lender ? `${corp}/${lender}` : (lender || corp || undefined);

        const metadata: Record<string, string> = {};
        if (options?.source) {
            metadata['source'] = options.source;
        }

        this.track_activity2(
            options?.action ?? 'View Lender',
            'lender',
            section,
            'view',
            url,
            {
                verb_id: ACTIVITY_VERB.VIEW,
                entity_id: ACTIVITY_ENTITY.LENDER,
                module_id: moduleId,
                slug_id: slugPair,
                ...(Object.keys(metadata).length ? { metadata } : {}),
            }
        );
    }

    trackActivityByRefs(
        verbId: number,
        entityId: number,
        moduleId: number,
        url: string,
        options?: { target_id?: number; slug_id?: string; metadata?: Record<string, any> | string }
    ): void {
        const action = `${getVerbLabel(verbId)} ${getEntityLabel(entityId)}`;
        const categorie = getEntityLabel(entityId);
        const section = getModuleLabel(moduleId);
        const type = getVerbLabel(verbId).toLowerCase();
        this.track_activity(action, categorie, section, type, url, {
            verb_id: verbId,
            entity_id: entityId,
            module_id: moduleId,
            ...options
        });
    }

    //@CacheBuster({ cacheBusterNotifier: ActivityService.cacheBuster$ })
    add_new_activity_feed(activityfeedpost: any) {
        return this.http.post<any>(this.serverUrl + 'registre/add_new_activity_feed', activityfeedpost, this.httpOptions)
            .pipe(
                catchError(this.handleError)
            );
    }

    /** Duplicata pour track_activity2 - migration progressive, ne pas casser l'existant */
    add_new_activity_feed2(activityfeedpost: any) {
        return this.http.post<any>(this.serverUrl + 'registre/add_new_activity_feed2', activityfeedpost, this.httpOptions)
            .pipe(
                catchError(this.handleError)
            );
    }
    //@Cacheable({ cacheBusterObserver: ActivityService.cacheBuster$ })
    get_all_activity_feed1(id: any = null) {
        return this.http.get<[]>(this.serverUrl + 'registre/get_all_activity_feed1' + (id ? ('/' + id) : ''))
            .pipe(
                catchError(this.handleError)
            );
    }
    //@CacheBuster({ cacheBusterNotifier: ActivityService.cacheBuster$ })
    get_all_activity_feed(searchbar: any) {
        return this.http.post<any>(this.serverUrl + 'registre/get_all_activity_feed', searchbar, this.httpOptions)
            .pipe(
                catchError(this.handleError)
            );
    }


    //@CacheBuster({ cacheBusterNotifier: ActivityService.cacheBuster$ })
    add_new_activity(data: any): Observable<any> {
        return this.http
            .post(
                this.serverUrl + 'registre/add_new_activity',
                data,
                this.httpOptions
            )
            .pipe(catchError(this.handleError));
    }
    get_all_activity(id: any = null) {
        return this.http
            .get<any>(this.serverUrl + 'registre/get_all_activity' + (id ? ('/' + id) : ''))
            .pipe(catchError(this.handleError));
    }

    get_users_login(facturationpost: any): any {
        return this.http
            .post<any[]>(
                this.serverUrl + 'local/get_users_login',
                facturationpost,
                this.httpOptions
            )
            .pipe(catchError(this.handleError));
    }

    get_all_users_login(facturationpost: any): any {
        return this.http
            .post<any[]>(
                this.serverUrl + 'local/get_all_users_login',
                facturationpost,
                this.httpOptions
            )
            .pipe(catchError(this.handleError));
    }
}